import { useNavigate } from '@tanstack/react-router';
import { Card, Flex, Typography } from 'antd';
import type { Sku } from '../../types';
import { formatPoints } from '../../utils';
import { AddToCartButton } from '../AddToCartButton/AddToCartButton';
import styles from './SkuCard.module.scss';

const { Text, Title } = Typography;

type Props = {
  sku: Sku;
  index?: number;
};

export const SkuCard = ({ sku, index = 0 }: Props) => {
  const navigate = useNavigate();
  const goToDetail = () => navigate({ to: '/market/$number', params: { number: String(sku.number) } });

  const delay = Math.min(index * 50, 180);
  const cover = sku.imageUrls[0] ?? null;
  const outOfStock = sku.stock <= 0;

  return (
    <Card
      hoverable
      className={styles.card}
      styles={{ body: { padding: 16 } }}
      style={{ animation: `slide-up var(--dur-normal) var(--ease-out) ${delay}ms both` }}
      cover={
        <button type="button" className={styles.cover} onClick={goToDetail} aria-label={sku.title}>
          {cover ? (
            <img
              src={cover}
              alt={sku.title}
              className={outOfStock ? `${styles.image} ${styles.imageOut}` : styles.image}
              loading="lazy"
            />
          ) : (
            <div className={styles.placeholder}>No image</div>
          )}
          {outOfStock ? (
            <span className={styles.outBadge}>
              <span className={styles.outBadgeLabel}>Out of stock</span>
            </span>
          ) : null}
        </button>
      }
    >
      <Flex vertical className={styles.body}>
        <Title
          level={5}
          className={styles.title}
          ellipsis={{ rows: 2, tooltip: sku.title }}
          onClick={goToDetail}
          style={{ margin: 0, cursor: 'pointer' }}
        >
          {sku.title}
        </Title>
        <Flex vertical gap={10} className={styles.footer}>
          <Text strong className={styles.price}>
            {formatPoints(sku.price)}
          </Text>
          <AddToCartButton sku={sku} block />
        </Flex>
      </Flex>
    </Card>
  );
};
